import * as THREE from "three";
import { OrbitControls, GLTFLoader, DRACOLoader, mergeBufferGeometries } from "three-stdlib";
import {
  AssemblyGraph,
  OccurrenceDef,
  StationId,
  StatusOverlay,
} from "../graph/types";
import { computeRenderState, diffStates, RenderState } from "../graph/renderState";
import { combinedBbox, fitCameraToBox } from "./fitCamera";
import { TweenRunner } from "./tween";
import {
  applyHighlight,
  makeBaseMaterial,
  targetOpacity,
  HighlightMode,
} from "./materials";

const TWEEN_MS = 600;

// One rendered instance of an occurrence: the glTF node cloned/placed, its own
// material, and the resolved target transform decomposed for tweening.
interface Placement {
  occId: string;
  object: THREE.Object3D;
  material: THREE.MeshStandardMaterial;
  finalPos: THREE.Vector3;
  finalQuat: THREE.Quaternion;
  finalScale: THREE.Vector3;
  installOffset: THREE.Vector3;
  installDelayMs: number;
  hideOnLandingOf: string | null;
  /** World-space geometry centroid at rest — the explode direction anchor. */
  worldCentroid: THREE.Vector3;
}

/**
 * Framework-free Three.js engine. The Lit element owns exactly one of these.
 * renderState(station) is idempotent and the only way geometry changes.
 */
export class AssemblyScene {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private tweens = new TweenRunner();
  private frameId: number | null = null;
  private dracoLoader: DRACOLoader | null = null;

  private graph: AssemblyGraph | null = null;
  private highlightMode: HighlightMode = "opacity";
  /** occId -> its placement (multiple placements can share a meshRef node). */
  private placements = new Map<string, Placement>();
  private current: RenderState | null = null;
  private overlayTint: StatusOverlay | undefined;

  // ── Toolbar view state ──────────────────────────────────────────────────────
  private modelBox = new THREE.Box3();       // world AABB of the whole model
  private assemblyCentroid = new THREE.Vector3();
  private modelMaxDim = 1;
  private explodeAmount = 0;                  // 0..1
  private clipPlane: THREE.Plane | null = null;
  private crossAxis: "x" | "y" | "z" | null = null;
  private capMesh: THREE.Mesh | null = null;      // stencil-filled solid cut face
  private vizMesh: THREE.Mesh | null = null;      // translucent cutting-plane
  private stencilHelpers: THREE.Mesh[] = [];      // per-part back/front stencil writers
  private edgeLines: THREE.LineSegments[] = [];   // per-part edge overlays

  constructor(private mount: HTMLElement) {
    const w = mount.clientWidth || 1;
    const h = mount.clientHeight || 1;

    // ---- renderer (seed ModelViewer.tsx 965-972) ----
    // stencil buffer required for the solid-capped cross-section (stencil technique).
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, stencil: true });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.renderer.domElement.style.cssText = "display:block;width:100%;height:100%";
    mount.appendChild(this.renderer.domElement);

    // ---- camera (seed 976-979) ----
    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 1000);
    this.camera.position.set(0, 0, 1);

    // ---- lighting rig (seed 981-986) ----
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.25));
    const hemi = new THREE.HemisphereLight(0xffffff, 0x606060, 0.3);
    hemi.position.set(0, 1, 0);
    this.scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(8, 12, 10);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.25);
    fill.position.set(-8, 4, -6);
    this.scene.add(fill);
    const rim1 = new THREE.DirectionalLight(0xffffff, 0.4);
    rim1.position.set(10, 3, 0);
    this.scene.add(rim1);
    const rim2 = new THREE.DirectionalLight(0xffffff, 0.35);
    rim2.position.set(-3, 8, -10);
    this.scene.add(rim2);

    // ---- controls (seed 988-999) ----
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.enablePan = true;
    this.controls.screenSpacePanning = true;
    this.controls.zoomToCursor = true;
    this.controls.minDistance = 0.01;
    this.controls.maxDistance = 50000;

    this.startLoop();
  }

  private startLoop(): void {
    const loop = () => {
      this.frameId = requestAnimationFrame(loop);
      this.tweens.update(performance.now());
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  /** Load the glb, index occurrence nodes, then render the initial station.
   *  `dracoPath` points at the folder holding the Draco decoder (so Draco-compressed
   *  GLBs decode); it can be omitted for uncompressed GLBs. */
  async load(glbUrl: string, graph: AssemblyGraph, initial: StationId, dracoPath?: string): Promise<void> {
    this.graph = graph;
    this.highlightMode = graph.config?.highlightMode ?? "opacity";

    const loader = new GLTFLoader();
    if (dracoPath) {
      const draco = new DRACOLoader();
      draco.setDecoderPath(dracoPath);
      loader.setDRACOLoader(draco);
      this.dracoLoader = draco;
    }
    const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
      loader.load(glbUrl, (g) => resolve(g as { scene: THREE.Group }), undefined, reject);
    });

    // Index every named node once; we clone per-occurrence so multiple
    // occurrences (e.g. 12 pistons) can reuse a single source node's geometry.
    const nodeByName = new Map<string, THREE.Object3D>();
    gltf.scene.traverse((o) => {
      if (o.name) nodeByName.set(o.name, o);
    });

    for (const [occId, occ] of Object.entries(graph.occurrences)) {
      const src = nodeByName.get(occ.meshRef);
      if (!src) {
        console.warn(`[AssemblyScene] occurrence "${occId}" -> missing glTF node "${occ.meshRef}"`);
        continue;
      }
      this.placements.set(occId, this.makePlacement(occId, occ, src));
    }

    // Everything starts hidden; renderState reveals the correct set.
    for (const p of this.placements.values()) {
      p.object.visible = false;
      this.scene.add(p.object);
    }

    // Whole-model AABB + centroid (for cross-section mapping and explode scale/direction).
    this.modelBox.makeEmpty();
    for (const p of this.placements.values()) {
      const b = new THREE.Box3().setFromObject(p.object);
      if (!b.isEmpty()) this.modelBox.union(b);
    }
    if (!this.modelBox.isEmpty()) {
      this.modelBox.getCenter(this.assemblyCentroid);
      const size = new THREE.Vector3();
      this.modelBox.getSize(size);
      this.modelMaxDim = Math.max(size.x, size.y, size.z);
    }

    this.renderState(initial, { animate: false });
    this.fitToVisible();
  }

  /** Fallback: clone the node hierarchy, applying one material to every mesh. */
  private cloneWithMaterial(src: THREE.Object3D, material: THREE.Material): THREE.Object3D {
    const object = src.clone(true);
    object.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.material = material;
    });
    return object;
  }

  /**
   * Merge all of a source node's primitive geometries (baked to the node's local space)
   * into a single Mesh with `material`. Returns null if the geometries are incompatible
   * (caller falls back to the multi-mesh clone).
   */
  private mergeToSingleMesh(src: THREE.Object3D, material: THREE.Material): THREE.Mesh | null {
    src.updateWorldMatrix(true, true);
    const inv = new THREE.Matrix4().copy(src.matrixWorld).invert();
    const geoms: THREE.BufferGeometry[] = [];
    src.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) return;
      // Bake the mesh's transform relative to the node, and keep only position+normal so
      // every geometry has an identical attribute set (mergeBufferGeometries requires it).
      const g = mesh.geometry.clone();
      const local = new THREE.Matrix4().multiplyMatrices(inv, mesh.matrixWorld);
      g.applyMatrix4(local);
      for (const attr of Object.keys(g.attributes)) {
        if (attr !== "position" && attr !== "normal") g.deleteAttribute(attr);
      }
      if (!g.getAttribute("normal")) g.computeVertexNormals();
      g.morphAttributes = {};
      geoms.push(g);
    });
    if (geoms.length === 0) return null;
    const merged = mergeBufferGeometries(geoms, false);
    geoms.forEach((g) => g.dispose());
    if (!merged) return null;
    return new THREE.Mesh(merged, material);
  }

  private makePlacement(occId: string, occ: OccurrenceDef, src: THREE.Object3D): Placement {
    const material = makeBaseMaterial();

    // Merge the source node's primitives (glTF splits a node into one mesh per
    // primitive — hundreds per part here) into a SINGLE mesh. This cuts the scene from
    // ~12k meshes to one per occurrence: a big general render win, and the only way the
    // stencil-capped cross-section (2 helper meshes per mesh) is feasible.
    const object = this.mergeToSingleMesh(src, material) ?? this.cloneWithMaterial(src, material);

    // Row-major 4x4 -> THREE (column-major) then decompose.
    const m = new THREE.Matrix4().fromArray(occ.finalTransform).transpose();
    const finalPos = new THREE.Vector3();
    const finalQuat = new THREE.Quaternion();
    const finalScale = new THREE.Vector3();
    m.decompose(finalPos, finalQuat, finalScale);

    object.position.copy(finalPos);
    object.quaternion.copy(finalQuat);
    object.scale.copy(finalScale);

    // World centroid at rest (geometry is baked in world space), used as the explode
    // direction anchor. Computed with the object at its final pose.
    object.updateWorldMatrix(true, true);
    const worldCentroid = new THREE.Vector3();
    const restBox = new THREE.Box3().setFromObject(object);
    if (!restBox.isEmpty()) restBox.getCenter(worldCentroid);
    else worldCentroid.copy(finalPos);

    return {
      occId,
      object,
      material,
      finalPos,
      finalQuat,
      finalScale,
      installOffset: new THREE.Vector3().fromArray(occ.installOffset),
      installDelayMs: occ.installDelayMs ?? 0,
      hideOnLandingOf: occ.hideOnLandingOf ?? null,
      worldCentroid,
    };
  }

  /**
   * The one entry point that changes geometry. Idempotent. `animate:false` snaps
   * (used for initial load); otherwise entering/leaving parts tween.
   */
  renderState(station: StationId, opts: { animate?: boolean } = {}): void {
    if (!this.graph) return;
    const animate = opts.animate ?? true;

    const next = computeRenderState(this.graph, station);
    const { entering, leaving, persisting } = diffStates(this.current, next);

    for (const id of entering) this.enter(id, next, animate);
    for (const id of leaving) this.leave(id, animate);
    for (const id of persisting) this.persist(id, next);

    this.overlayTint = next.overlay;
    this.applyOverlay();
    // Reframe to the set that WILL be visible (using final transforms so framing is
    // stable and doesn't chase mid-tween positions). Only when the set actually
    // changed, so idempotent re-renders don't nudge the user's camera.
    const changed = entering.length > 0 || leaving.length > 0;
    this.current = next;
    if (changed) this.frameSet(next.visible);
  }

  /** Fit the camera to where the visible set will REST (final positions). */
  private frameSet(ids: Set<string>): void {
    const box = new THREE.Box3();
    let any = false;
    const delta = new THREE.Vector3();
    for (const id of ids) {
      const p = this.placements.get(id);
      if (!p) continue;
      p.object.updateWorldMatrix(true, true);
      // Real world AABB of the actual geometry (handles vertices baked in world space,
      // where object.position is the transform translation, not the geometry location).
      const b = new THREE.Box3().setFromObject(p.object);
      if (b.isEmpty()) continue;
      // The object may be mid-tween (at installOffset). Shift its box by the remaining
      // travel so we frame the RESTING pose (final + explode), not the transient start.
      delta.subVectors(this.restPos(p), p.object.position);
      b.translate(delta);
      box.union(b);
      any = true;
    }
    // While auto-rotating, keep the current spin angle across the reframe instead of
    // snapping back to the default view.
    if (any && !box.isEmpty()) {
      fitCameraToBox(this.camera, this.controls, box, { preserveDir: this.controls.autoRotate });
    }
  }

  private startPos(p: Placement): THREE.Vector3 {
    return p.finalPos.clone().add(p.installOffset);
  }

  /** Outward explode offset for a part at the current explodeAmount (0 when off). */
  private explodeVec(p: Placement): THREE.Vector3 {
    if (this.explodeAmount <= 0) return new THREE.Vector3();
    const dir = p.worldCentroid.clone().sub(this.assemblyCentroid);
    if (dir.lengthSq() < 1e-6) return new THREE.Vector3();
    return dir.normalize().multiplyScalar(this.explodeAmount * this.modelMaxDim * 0.5);
  }

  /** Where a part should REST — its final pose plus any current explode offset. */
  private restPos(p: Placement): THREE.Vector3 {
    return p.finalPos.clone().add(this.explodeVec(p));
  }

  /**
   * Target opacity for an occurrence at `next`. QC stations (those carrying a
   * statusOverlay) present the finished product, so every part is fully opaque —
   * no dimming, no highlight contrast. Otherwise the normal highlight rule applies.
   */
  private opacityFor(id: string, next: RenderState): number {
    if (next.overlay) return 1;
    return targetOpacity(next.newThisStation.has(id), this.highlightMode);
  }

  private enter(id: string, next: RenderState, animate: boolean): void {
    const p = this.placements.get(id);
    if (!p) return;
    const isNew = next.newThisStation.has(id) && !next.overlay;
    p.object.visible = true;
    applyHighlight(p.material, { isNew, mode: this.highlightMode });
    const opTarget = this.opacityFor(id, next);

    const rest = this.restPos(p);
    this.tweens.cancel(`pos:${id}`);
    if (!animate) {
      p.object.position.copy(rest);
      p.material.opacity = opTarget;
      return;
    }

    const from = this.startPos(p);
    p.object.position.copy(from);
    const fromOp = 0;
    p.material.opacity = fromOp;
    // A future startMs holds the part at `from` with opacity 0 (invisible) until its
    // delay elapses (smoothstep clamps raw<0 to 0), then it animates in. This staggers
    // parts entering at the same station without breaking the pure resting state.
    this.tweens.set({
      key: `pos:${id}`,
      startMs: performance.now() + p.installDelayMs,
      durationMs: TWEEN_MS,
      step: (e) => {
        p.object.position.lerpVectors(from, rest, e);
        p.material.opacity = fromOp + (opTarget - fromOp) * e;
      },
    });
  }

  private leave(id: string, animate: boolean): void {
    const p = this.placements.get(id);
    if (!p) return;
    applyHighlight(p.material, { isNew: false, mode: this.highlightMode });
    this.tweens.cancel(`pos:${id}`);
    this.tweens.cancel(`op:${id}`);

    // Cut-exit for a preview stand-in: don't animate out. Hold it exactly where it is,
    // then instantly hide it the moment its real replacement finishes landing.
    if (p.hideOnLandingOf) {
      if (!animate) { p.object.visible = false; return; }
      const other = this.placements.get(p.hideOnLandingOf);
      const landMs = (other?.installDelayMs ?? 0) + TWEEN_MS;
      this.tweens.set({
        key: `pos:${id}`,
        startMs: performance.now(),
        durationMs: landMs,
        step: () => {}, // hold in place, no motion or fade
        done: () => { p.object.visible = false; },
      });
      return;
    }

    if (!animate) {
      p.object.visible = false;
      return;
    }
    const from = p.object.position.clone();
    const to = this.startPos(p);
    const fromOp = p.material.opacity;
    this.tweens.set({
      key: `pos:${id}`,
      startMs: performance.now(),
      durationMs: TWEEN_MS,
      step: (e) => {
        p.object.position.lerpVectors(from, to, e);
        p.material.opacity = fromOp * (1 - e);
      },
      done: () => {
        p.object.visible = false;
      },
    });
  }

  private persist(id: string, next: RenderState): void {
    const p = this.placements.get(id);
    if (!p) return;
    const isNew = next.newThisStation.has(id) && !next.overlay;
    applyHighlight(p.material, { isNew, mode: this.highlightMode });
    const opTarget = this.opacityFor(id, next);

    // If not mid-tween, snap position to rest (final + any explode); always tween opacity.
    if (!this.tweens.has(`pos:${id}`)) p.object.position.copy(this.restPos(p));

    const fromOp = p.material.opacity;
    if (Math.abs(fromOp - opTarget) < 0.001) {
      p.material.opacity = opTarget;
      return;
    }
    this.tweens.set({
      key: `op:${id}`,
      startMs: performance.now(),
      durationMs: TWEEN_MS,
      step: (e) => {
        p.material.opacity = fromOp + (opTarget - fromOp) * e;
      },
    });
  }

  private applyOverlay(): void {
    // v1: QC overlays are status-only and add no geometry. Keep the full assembly
    // opaque at QC (nothing is being singled out). Hook for a colored tint/badge.
    if (!this.overlayTint) return;
    // Intentionally minimal for v1 — the Lit element renders the QC status badge.
  }

  private fitToVisible(): void {
    const visible = [...this.placements.values()]
      .filter((p) => p.object.visible)
      .map((p) => p.object);
    const box = combinedBbox(visible.length ? visible : [...this.placements.values()].map((p) => p.object));
    if (box) fitCameraToBox(this.camera, this.controls, box);
  }

  resize(): void {
    const w = this.mount.clientWidth;
    const h = this.mount.clientHeight;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  /** Programmatic "reset view" — refit to whatever is currently visible. */
  fitCamera(): void {
    this.fitToVisible();
  }

  // ── Toolbar controls ────────────────────────────────────────────────────────

  /** Spin the model about its up axis. */
  setAutoRotate(on: boolean): void {
    this.controls.autoRotate = on;
    this.controls.autoRotateSpeed = 2.0;
  }

  /** Reset view = stop spinning and reframe to the current visible set. */
  resetView(): void {
    this.controls.autoRotate = false;
    this.fitToVisible();
  }

  /**
   * Cross-section clip with a SOLID cut face — the stencil-capped technique ported from
   * the Makistry ModelViewer. axis null clears it. For each part, two invisible helper
   * meshes write the stencil (back-faces INCR, front-faces DECR under the clip); a single
   * cap plane then fills only where the stencil is non-zero, so the cut reads as solid
   * material rather than hollow. A faint blue plane visualises the cutting plane.
   */
  setCrossSection(axis: "x" | "y" | "z" | null, t = 0.5): void {
    if (!axis || this.modelBox.isEmpty()) {
      this.clearCrossSection();
      return;
    }
    const i = axis === "x" ? 0 : axis === "y" ? 1 : 2;
    const min = this.modelBox.min.getComponent(i);
    const max = this.modelBox.max.getComponent(i);
    const range = max - min;
    const pad = range * 0.1; // extend cleanly past the bbox at both ends
    const planePos = min - pad + (range + pad * 2) * Math.min(1, Math.max(0, t));

    // Rebuild helpers/cap only when the axis changes; a same-axis move just repositions.
    const rebuild = axis !== this.crossAxis || !this.capMesh || !this.vizMesh;
    if (rebuild) this.clearCrossSection(); // nulls clipPlane — recreate it below

    const normal = new THREE.Vector3();
    normal.setComponent(i, -1); // keep the lower side, cut the upper -> see inside
    if (!this.clipPlane) this.clipPlane = new THREE.Plane();
    this.clipPlane.set(normal, planePos);

    if (!rebuild) {
      // Helpers/materials reference the shared clipPlane (just updated); move cap + viz.
      this.positionCap(this.capMesh!, i, planePos);
      this.positionCap(this.vizMesh!, i, planePos);
      return;
    }

    this.crossAxis = axis;
    this.renderer.localClippingEnabled = true;

    // Clip every part material (station-independent — new visible parts clip too).
    for (const p of this.placements.values()) {
      p.material.clippingPlanes = [this.clipPlane];
      p.material.needsUpdate = true;
    }

    // Stencil writers per mesh: back-face INCR, front-face DECR (both clipped).
    // Collect the ORIGINAL meshes first — adding helpers mid-traverse would recurse
    // into the freshly-added helper meshes and spawn them without bound.
    const STENCIL_ORDER = 1;
    const sourceMeshes: THREE.Mesh[] = [];
    for (const p of this.placements.values()) {
      p.object.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) sourceMeshes.push(o as THREE.Mesh);
      });
    }
    for (const src of sourceMeshes) {
      for (const [side, op] of [
        [THREE.BackSide, THREE.IncrementWrapStencilOp],
        [THREE.FrontSide, THREE.DecrementWrapStencilOp],
      ] as const) {
        const mat = new THREE.MeshBasicMaterial({
          depthWrite: false,
          depthTest: false,
          colorWrite: false,
          stencilWrite: true,
          stencilFunc: THREE.AlwaysStencilFunc,
          side,
          clippingPlanes: [this.clipPlane!],
        });
        mat.stencilFail = mat.stencilZFail = mat.stencilZPass = op;
        const helper = new THREE.Mesh(src.geometry, mat);
        helper.renderOrder = STENCIL_ORDER;
        src.add(helper); // child -> inherits the part's transform + visibility
        this.stencilHelpers.push(helper);
      }
    }

    // Cap plane: fills where stencil != 0 (the cut face) with neutral gray.
    const size = new THREE.Vector3();
    this.modelBox.getSize(size);
    const planeSize = Math.max(size.x, size.y, size.z) * 2.5;
    const capMat = new THREE.MeshStandardMaterial({
      color: 0xb8b8b8,
      metalness: 0.1,
      roughness: 0.35,
      side: THREE.DoubleSide,
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: THREE.NotEqualStencilFunc,
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilZPass: THREE.ReplaceStencilOp,
      depthWrite: false,
    });
    const cap = new THREE.Mesh(new THREE.PlaneGeometry(planeSize, planeSize), capMat);
    cap.renderOrder = 1.1;
    this.positionCap(cap, i, planePos);
    this.scene.add(cap);
    this.capMesh = cap;

    // Translucent cutting-plane visualisation.
    const vizMat = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const viz = new THREE.Mesh(new THREE.PlaneGeometry(planeSize, planeSize), vizMat);
    this.positionCap(viz, i, planePos);
    this.scene.add(viz);
    this.vizMesh = viz;
  }

  /** Orient a plane mesh perpendicular to world axis i, centred at planePos. */
  private positionCap(mesh: THREE.Mesh, i: number, planePos: number): void {
    const c = new THREE.Vector3();
    this.modelBox.getCenter(c);
    mesh.rotation.set(0, 0, 0);
    if (i === 0) {
      mesh.rotation.y = Math.PI / 2;
      mesh.position.set(planePos, c.y, c.z);
    } else if (i === 1) {
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(c.x, planePos, c.z);
    } else {
      mesh.position.set(c.x, c.y, planePos);
    }
  }

  private clearCrossSection(): void {
    for (const h of this.stencilHelpers) {
      h.removeFromParent();
      (h.material as THREE.Material).dispose(); // geometry is shared with the part — don't dispose
    }
    this.stencilHelpers = [];
    for (const m of [this.capMesh, this.vizMesh]) {
      if (!m) continue;
      this.scene.remove(m);
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.capMesh = null;
    this.vizMesh = null;
    for (const p of this.placements.values()) {
      p.material.clippingPlanes = [];
      p.material.needsUpdate = true;
    }
    this.clipPlane = null;
    this.crossAxis = null;
    this.renderer.localClippingEnabled = false;
  }

  /** Explode the assembly outward from its centroid. amount∈[0,1]. Live (no tween). */
  setExplode(amount: number): void {
    this.explodeAmount = Math.min(1, Math.max(0, amount));
    for (const [id, p] of this.placements) {
      if (!p.object.visible) continue;
      // Don't fight an in-flight install/leave tween; those target restPos already.
      if (this.tweens.has(`pos:${id}`)) continue;
      p.object.position.copy(this.restPos(p));
    }
  }

  /** Overlay black feature edges on every part. Lines are children so they follow the
   *  part through install/explode; visibility follows the parent part. */
  setEdges(on: boolean): void {
    if (on && this.edgeLines.length === 0) {
      const mat = new THREE.LineBasicMaterial({ color: 0x1f2937 });
      for (const p of this.placements.values()) {
        p.object.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh || !mesh.geometry) return;
          const edges = new THREE.EdgesGeometry(mesh.geometry, 30); // 30° threshold
          const lines = new THREE.LineSegments(edges, mat);
          mesh.add(lines);
          this.edgeLines.push(lines);
        });
      }
    }
    for (const l of this.edgeLines) l.visible = on;
  }

  dispose(): void {
    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.tweens.clear();
    this.clearCrossSection();
    for (const l of this.edgeLines) { l.removeFromParent(); l.geometry.dispose(); }
    this.edgeLines = [];
    this.controls.dispose();
    for (const p of this.placements.values()) {
      p.object.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) m.geometry?.dispose();
      });
      p.material.dispose();
    }
    this.placements.clear();
    this.dracoLoader?.dispose();
    this.renderer.dispose();
    if (this.renderer.domElement.parentElement === this.mount) {
      this.mount.removeChild(this.renderer.domElement);
    }
  }
}
